// The hold-fire recipe kit: the input every hold-fire look draws from, and
// the arithmetic it draws with (R1439, `ui-map-interface.md` §6.1.7).
//
// A HOLD-FIRE look is what an own fleet draws around its hull while it holds
// fire (R1437). The World theme field `holdFire` names one of five, the
// cards the owner picked from `archive/mockups/063-hold-fire.html`.
// `holdfire-styles.ts` is the registry that answers a look id, and
// `holdfire-recipes.ts` holds the draw code. This module is what both of
// them import.
//
// This is a leaf (`leaves.test.ts`). It names the two sinks of a layer
// through a type-only clause, the way `anchor-recipe-kit.ts` does. The fleet
// layer owns the flag, the clock, the cull and the batches, and it hands them
// in as ONE reused context a hull.
//
// **Nothing here allocates on the frame path.** A recipe runs once per own
// hull that holds fire, every frame (§10.1).
//
// **The look is own-only.** `FleetState.hold_fire` rides to the owner alone,
// so the context carries no faction, no ink of a hull and no fainter alpha.
// Every look wears the one pale ink of `[holdfire.limits]`.
//
// **Every hull runs on one clock.** The context carries the layer's wall
// clock and no phase a hull, so twenty hulls that hold fire move in step,
// the rule of every card of round 063 that the owner picked.
//
// ---------------------------------------------------------------------------
// HOW A MOCKUP CARD MAPS ONTO THE KIT
// ---------------------------------------------------------------------------
//
// The cards of round 063 draw in WORLD px with the hull at the origin, on a
// canvas that is NOT turned. The translation is fixed:
//
//   | the card writes                  | the recipe writes                |
//   | -------------------------------- | -------------------------------- |
//   | `x`, `y`                         | `c.x`, `c.y`                     |
//   | `t`                              | `c.t`, WALL seconds              |
//   | `st.z`                           | `c.z`                            |
//   | `o.sel`                          | `c.selected`                     |
//   | `TELL`                           | `HOLD_FIRE_LIMITS.ink`           |
//   | `lw(w)`                          | `lw(c, w)`                       |
//   | `wave(t, period, ph)`            | `wave(t, period, ph)`            |
//   | `circle(c, x, y, r, …)`, no dash | `ring(c, r, w, a)`               |
//   | `arc(c, x, y, r, a0, a1, …)`     | `arcOf(c, r, w, a0, a1 - a0, a)` |
//   | `line(c, …)`                     | `line(c, …)` or `spoke(c, …)`    |
//   | `text(c, 'guns housed', …)`      | `c.legend(…)`                    |
//
// Every FIGURE came out of the card and stands in the look's row of
// `data/presentation/holdfire.toml`. Every RATIO stays in the recipe file as
// a named constant, the rule of the anchoring recipes (R1125).

import { HOLD_FIRE_LIMITS } from '../gen/presentation';
import type { HoldFireRow } from '../gen/presentation';
import type { CircleSink, SegmentSink } from '../fast/layers/batches';
import { TAU, strokeFor } from './recipe-kit';

export type { HoldFireRow };
export { TAU, frac } from './recipe-kit';

/**
 * Where the legend of `restricted-line` goes. The fleet layer answers it
 * with the chart's italic glyph cells (§17), so the map bakes no second
 * italic face. The run is CENTRED on `(x, y)`, in WORLD px, so it grows with
 * the map the way the card's text does.
 */
export type HoldFireLegend = (x: number, y: number, sizePx: number, ink: number, alpha: number) => void;

/**
 * ONE OWN HULL THAT HOLDS FIRE, as a recipe reads it. The layer reuses ONE of
 * these (R131).
 *
 * Every length is world px and every angle is radians, with +x east and +y
 * south. An angle turns clockwise on the screen, the canvas rule.
 */
export interface HoldFireCtx {
  /** The layer's clock in WALL seconds. Every hull reads the same clock. */
  t: number;
  /** The camera scale: CSS px for one world px. */
  z: number;
  /** The figure row of the look that draws. */
  row: HoldFireRow;
  /** The engine's round-cap flag (`fast/layer.ts` `F_ROUND`), handed in so
   *  this leaf takes no engine import. */
  round: number;
  /** The hull's centre, in world px. */
  x: number;
  y: number;
  /** True on the selected hull. Only `restricted-line` reads it. */
  selected: boolean;
  segs: SegmentSink;
  rings: CircleSink;
  /** The legend sink. */
  legend: HoldFireLegend;
}

/** ONE HOLD-FIRE LOOK: what it draws for one hull in one frame. */
export type HoldFireRecipe = (c: HoldFireCtx) => void;

// ---------------------------------------------------------------------------
// The arithmetic
// ---------------------------------------------------------------------------

/** A breath from 0 to 1 and back over `period` seconds, offset by `ph` of a
 *  period. It is `wave` of the page. */
export function wave(t: number, period: number, ph = 0): number {
  return 0.5 + 0.5 * Math.sin(TAU * (t / period + ph));
}

/**
 * The drawn width of a stroke the card gave as `w`: never under
 * `min_screen_px` on the screen. It is `lw` of the page, so a look still
 * draws a line at zoom 0.5.
 */
export function lw(c: HoldFireCtx, w: number): number {
  return Math.max(w, HOLD_FIRE_LIMITS.minScreenPx / c.z);
}

// ---------------------------------------------------------------------------
// The primitives
// ---------------------------------------------------------------------------
//
// Each one clamps its alpha under the ceiling of `[holdfire.limits]`, puts
// every width through `lw`, wears the one ink and skips a mark that would
// draw nothing, so a recipe states the card's figure and never guards it.

/** A stroke alpha, held under the ceiling. */
export const strokeAlpha = strokeFor(HOLD_FIRE_LIMITS);

/** A stroked full circle. It is `circle` of the page with no dash. */
export function ring(c: HoldFireCtx, r: number, w: number, a: number): void {
  const al = strokeAlpha(a);
  if (r <= 0 || w <= 0 || al <= 0) return;
  c.rings.ring(c.x, c.y, r, lw(c, w), HOLD_FIRE_LIMITS.ink, al);
}

/** A stroked partial circle round the hull, from `start` over `sweep`
 *  radians. The page's `arc` takes an END, so a port passes `a1 - a0`. */
export function arcOf(
  c: HoldFireCtx, r: number, w: number, start: number, sweep: number, a: number,
): void {
  const al = strokeAlpha(a);
  if (r <= 0 || w <= 0 || sweep === 0 || al <= 0) return;
  c.rings.arc(c.x, c.y, r, lw(c, w), start, sweep, HOLD_FIRE_LIMITS.ink, al);
}

/**
 * A straight stroke in world px. `round` asks for the card's round caps; a
 * hatch stroke that the card clips to a disc passes false, so no cap passes
 * the rim. It is `line` of the page.
 */
export function line(
  c: HoldFireCtx, x0: number, y0: number, x1: number, y1: number,
  w: number, a: number, round = true,
): void {
  const al = strokeAlpha(a);
  if (w <= 0 || al <= 0) return;
  c.segs.line(x0, y0, x1, y1, lw(c, w), HOLD_FIRE_LIMITS.ink, al, round ? c.round : 0);
}

/** A radial stroke round the hull at angle `a`, from radius `r0` to `r1`. */
export function spoke(
  c: HoldFireCtx, ang: number, r0: number, r1: number, w: number, a: number,
): void {
  const ca = Math.cos(ang), sa = Math.sin(ang);
  line(c, c.x + ca * r0, c.y + sa * r0, c.x + ca * r1, c.y + sa * r1, w, a);
}
